import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import styled, { keyframes } from 'styled-components';
import {
  UserPet,
  PetTemplate,
  PetAction,
  PetInteractionLog,
  ActionCooldown,
  LevelSystem,
} from '@typings/pets/pet.interface';
import Header from './Header';
import ItemControls from './ItemControls';
import { InteractionLog } from '../components';
import { useCollection, useDocument, fuego } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { currentUserIDState } from '@atom/setting/user';
import { useRecoilValue } from 'recoil';
import PetActionPanel from '../components/ActionPanel/PetActionPanel';
import { useRecoilState } from 'recoil';
import { petFloatingActiveState } from '@atom/setting/petFloating';
import LevelUpModal from '../components/LevelUpModal';
import useEvolutionSystem from '../hooks/useEvolutionSystem';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import { petOpenState } from '@atom/setting/jukebox';
import PetDisplay from '../components/PetDisplay';
import useLevelCheck from '../hooks/useLevelCheck';
import useAutoLevelUp from '../hooks/useAutoLevelUp';
import { usePetLabel } from '../hooks/usePetLabel';

const PetView: React.FC = () => {
  // 🐾 상태 관리
  const [userPets, setUserPets] = useState<UserPet[]>([]);
  const [activePetId, setActivePetId] = useState<string>('');
  const [open, setOpen] = useRecoilState(petOpenState);

  const { petLabel } = usePetLabel();
  const [isFloating, setIsFloating] = useRecoilState(petFloatingActiveState);
  const [petTemplates, setPetTemplates] = useState<PetTemplate[]>([]);

  // 액션 설정
  const [interactionLogs, setInteractionLogs] = useState<PetInteractionLog[]>(
    [],
  );

  const [evolveFxTick, setEvolveFxTick] = useState<number>(0);


  // 현재 유저 ID
  const currentUid = useRecoilValue(currentUserIDState);

  const [levelUpInfo, setLevelUpInfo] = useState<{
    from: number;
    to: number;
  } | null>(null);
  const [lastLevelUpInfo, setLastLevelUpInfo] = useState<{
    from: number;
    to: number;
  } | null>(null);
  const [levelupModalOpen, setLevelupModalOpen] = useState<boolean>(false);

  // 레벨 설정 불러오기
  const { data: levelSystemDoc } = useDocument<LevelSystem>(
    `${CollectionPath.PETSETTING}/levelSystem`,
    { listen: true },
  );

  // 내 보유 펫 실시간 로드
  const petsColPath = currentUid
    ? `${CollectionPath.USERS}/${currentUid}/pets`
    : ``;
  const { data: ownedPets } = useCollection<UserPet>(petsColPath, {
    listen: true,
    orderBy: ['createdAt', 'desc'],
  });

  // 보유 펫 → 상태 반영 및 활성 펫 선택
  useEffect(() => {
    if (!ownedPets) return;
    setUserPets(ownedPets);

    // 기존 활성 펫 유지하되 없으면 첫 펫으로
    const nextActiveId =
      ownedPets.find((p) => p.isActive)?.id || ownedPets[0]?.id || '';
    setActivePetId((prev) =>
      prev && ownedPets.some((p) => p.id === prev) ? prev : nextActiveId,
    );
  }, [ownedPets]);

  // 🎯 활성 펫 정보
  const activePet = useMemo(
    () => userPets.find((pet) => pet.id === activePetId),
    [userPets, activePetId],
  );

  // 현재 활성 펫의 템플릿 문서 구독 (진화 트리 사용)
  const { data: currentTemplateDoc } = useDocument<PetTemplate>(
    activePet?.petTemplateId
      ? `${CollectionPath.PET}/${activePet.petTemplateId}`
      : null,
    { listen: true },
  );

  const levelKey = useMemo(() => {
    return activePet?.level ? `level${activePet.level}` : '';
  }, [activePet?.level]);

  const evolutionOptions = useMemo(() => {
    const trees: any = (currentTemplateDoc as any)?.evolutionTrees;
    if (!trees || !levelKey) return [] as any[];
    const options = trees[levelKey];
    return Array.isArray(options) ? options : [];
  }, [currentTemplateDoc, levelKey]);

  const canEvolve = useMemo(() => {
    if (!activePet) return false;
    if (!Array.isArray(evolutionOptions) || evolutionOptions.length === 0)
      return false;
    // 확장성: 요구 레벨/아이템 조건 중 레벨만 먼저 체크
    const eligible = evolutionOptions.some(
      (opt: any) =>
        Number(opt?.requiredLevel || activePet.level) <=
        Number(activePet.level),
    );
    return eligible;
  }, [activePet, evolutionOptions]);

  // 펫 레벨업
  const { getMaxExpForLevel, computeLevelFromExp, isExpMaxForLevel } =
    useLevelCheck(levelSystemDoc as any);

  // 경험치 임계치 도달 시 자동 레벨업 처리 (공통 훅)
  useAutoLevelUp({
    currentUid,
    activePet: activePet as any,
    levelSystem: levelSystemDoc as any,
    setUserPets,
    onLevelUp: (from, to) => {
      const info = { from, to };
      setLevelUpInfo(info);
      setLastLevelUpInfo(info);
    },
  });

  const isExpMaxForLevelMemo = useMemo(() => {
    if (!activePet) return false;
    return isExpMaxForLevel(
      Number(activePet.experience || 0),
      Number(activePet.level || 1),
    );
  }, [activePet, isExpMaxForLevel]);

  // 현재 활성 펫 문서 path 및 set 함수 (저장용)
  const petDocPath =
    currentUid && activePetId
      ? `${CollectionPath.USERS}/${currentUid}/pets/${activePetId}`
      : '';
  const { set: setActivePetDoc } = useDocument<UserPet>(petDocPath, {
    listen: false,
  });


  // ===================== 진화 체크 & 모달 (Hook로 분리) =====================
  const {
    evoPrompt,
    tryOpenEvolutionPrompt,
    handleConfirmEvolution,
    handleCancelEvolution,
  } = useEvolutionSystem({
    activePet,
    petTemplates,
    petDocPath,
    setActivePetDoc: setActivePetDoc as any,
    currentUid: currentUid || null,
    setUserPets,
    setInteractionLogs,
  });

  useEffect(() => {
    tryOpenEvolutionPrompt();
  }, [activePet?.level, activePet?.experience, tryOpenEvolutionPrompt]);

  if (!activePet) {
    return (
      <EmptyContainer>
        <CloseBtn
          onClick={() => setOpen(false)}
          size="sm"
          shape="stroke"
          fixed
        />
        <EmptyIcon>🐾</EmptyIcon>
        <EmptyText>{petLabel}을(를) 생성해주세요</EmptyText>
      </EmptyContainer>
    );
  }

  return (
    <Container>
      {/* 상단 헤더 */}
      <Header
        userPets={userPets}
        activePetId={activePetId}
        setUserPets={setUserPets}
        setActivePetId={setActivePetId}
        isFloating={isFloating}
        onToggleFloating={(next) => setIsFloating(next)}
        onToggleLevelupModal={() => setLevelupModalOpen((v) => !v)}
      />

      {/* 메인 펫 영역 */}
      <GameArea>
        <LevelUpModal
          info={levelUpInfo}
          onClose={() => {
            setLevelUpInfo(null);
            setLevelupModalOpen(false);
          }}
          levelSystem={levelSystemDoc as any}
          canEvolve={Boolean(canEvolve && isExpMaxForLevelMemo)}
          activePet={activePet}
          isOpen={levelupModalOpen || Boolean(levelUpInfo)}
        />

        <ActionsCard>
          {/* 액션 버튼들 */}
          <PetActionPanel activePet={activePet} />
        </ActionsCard>
        <PetDisplay
          pet={activePet}
          isFloating={isFloating}
          onPetClick={() => console.log('펫 클릭!')}
        />
        {evolveFxTick > 0 && (
          <EvolveFxOverlay>
            <EvolveFxSparkle />
            {Array.from({ length: 24 }).map((_, i) => {
              const deg = Math.round((360 / 24) * i);
              const delay = (i % 6) * 35;
              const colors = [
                '#FF5252',
                '#FFEB3B',
                '#69F0AE',
                '#40C4FF',
                '#7C4DFF',
              ];
              const color = colors[i % colors.length];
              return (
                <EvoConfetti
                  key={`evo_cf_${i}`}
                  style={{
                    ['--deg' as any]: `${deg}deg`,
                    ['--delay' as any]: `${delay}ms`,
                    ['--color' as any]: color,
                  }}
                />
              );
            })}
          </EvolveFxOverlay>
        )}
      </GameArea>

      <StatusCard>
        {/* 상태바 */}
        <ItemControls
          levelSystemData={levelSystemDoc}
          activePet={activePet}
          setUserPets={setUserPets}
        />

        {/* 하단 로그 */}
        <InteractionLog
          logs={interactionLogs}
          maxHeight={180}
          showFilter={true}
          autoScroll={true}
          activePet={activePet}
        />
      </StatusCard>
    </Container>
  );
};
export default PetView;

// 🎨 스타일 컴포넌트
const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr 1fr;
  height: 100%;
`;

// BackgroundImage 제거 (미사용)

const GameArea = styled.div`
  position: relative;
  text-align: center;
`;

const ActionsCard = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 16px;
  z-index: 2;
  pointer-events: none;

  /* 내부 버튼들은 클릭 가능하도록 */
  > * {
    pointer-events: auto;
  }
`;

const StatusCard = styled.div`
  background: var(--cardBackgroundColor);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 16px;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  opacity: 0.5;
`;

const EmptyText = styled.div`
  font-size: 18px;
  color: var(--subTextColor);
`;

// Floating evolve button removed (migrated into LevelUpModal)

// Evolution particle FX (mini)
const fxBurst = keyframes`
  0% { opacity: 1; transform: translate(-50%, -50%) rotate(var(--deg)) translateY(0) scale(0.8); }
  100% { opacity: 0; transform: translate(-50%, -50%) rotate(var(--deg)) translateY(-100px) scale(1); }
`;

const EvolveFxOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const EvolveFxSparkle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 215, 0, 0.35),
    rgba(255, 215, 0, 0) 60%
  );
  transform: translate(-50%, -50%);
`;

const EvoConfetti = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color);
  transform: translate(-50%, -50%);
  opacity: 0;
  animation: ${fxBurst} 700ms ease-out forwards;
  animation-delay: var(--delay, 0ms);
`;
